<template>
  <main
    id="app"
    :class="[
      'container',
      isShopPage && 'container--shop',
      showShopDefaultHeroSection && isShopPage && 'container--shop-default-hero',
      isSkillDivePreSaleActive &&
        !userHasSkillDive &&
        (isShopPage || isSkillDivePreSalePage) &&
        !isOnePlanAvailable &&
        'container--skill-dive-hero',
      isSkillDivePreSalePage && 'container--skill-dive-page',
      isTrialPage && 'container--trial',
      isAccountPage && 'container--account',
      isPaymentPage && 'container--payment',
      isConfirmationPage && 'container--confirmation',
      isCompletePage && 'container--complete',
      isEDAPremiumPreSaleActive &&
        !isAuthenticated &&
        (isShopPage || isEDAPremiumPage) &&
        'container--eda-premium-pre-sale',
    ]"
  >
    <div v-if="isModalOpen" class="container__mask"></div>
    <TheHeader v-if="isHeaderShown" />
    <ShopHeader v-if="isShopPage" />
    <PremiumMonthlyPromoSection
      v-if="
        !isOnePlanAvailable &&
        isPremiumMonthlyPromoActive &&
        (isShopPage || isPremiumMonthlyPromoPage)
      "
    />
    <EDAPremiumPreSaleSection
      v-if="!isOnePlanAvailable && isEDAPremiumPreSaleActive && (isShopPage || isEDAPremiumPage)"
    />
    <ShopDefaultHeroSection v-if="newDesign && showShopDefaultHeroSection && isShopPage" />
    <SkillDiveHeroSection
      v-if="
        newDesign &&
        isSkillDivePreSaleActive &&
        !userHasSkillDive &&
        !isOnePlanAvailable &&
        (isShopPage || isSkillDivePreSalePage)
      "
    />
    <transition name="fade" mode="out-in">
      <router-view :key="$route.name" />
    </transition>
    <PaymentForm v-if="isPaymentModalShown" />
    <PaymentSelectorModal v-if="isPaymentSelectorModalShown" />
    <SignInModal v-if="isSignInModalShown" />
    <div id="recurly-3ds" :style="isIn3dsFlow ? 'display: block' : 'display: none'"></div>
    <ShopFooter v-if="showShopFooter" />
    <PaymentFlowFooter v-else />
  </main>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import TheHeader from '@/components/flow/TheHeader.vue'
import ShopHeader from '@/components/flow/ShopHeader.vue'
import PaymentForm from '@/components/PaymentForm.vue'
import PaymentSelectorModal from '@/components/PaymentSelectorModal.vue'
import ShopFooter from '@/components/flow/ShopFooter.vue'
import PaymentFlowFooter from '@/components/PaymentFlowFooter.vue'
import PremiumMonthlyPromoSection from '@/components/hero-sections/PremiumMonthlyPromoSection.vue'
import EDAPremiumPreSaleSection from '@/components/hero-sections/EDAPremiumPreSaleSection.vue'
import ShopDefaultHeroSection from '@/components/hero-sections/ShopDefaultHeroSection.vue'
import SkillDiveHeroSection from '@/components/hero-sections/SkillDiveHeroSection.vue'
import SignInModal from '@/components/login/SignInModal.vue'

export default {
  name: 'MainLayout',
  components: {
    TheHeader,
    ShopHeader,
    PaymentForm,
    PaymentSelectorModal,
    ShopFooter,
    PaymentFlowFooter,
    PremiumMonthlyPromoSection,
    EDAPremiumPreSaleSection,
    ShopDefaultHeroSection,
    SkillDiveHeroSection,
    SignInModal,
  },
  computed: {
    ...mapState({
      isIn3dsFlow: state => state.subscriptions.isIn3dsFlow,
      isEditModalShown: state => state.app.isEditModalShown,
      isPaymentModalShown: state => state.app.isPaymentModalShown,
      isPaymentSelectorModalShown: state => state.app.isPaymentSelectorModalShown,
      isOnePlanAvailable: state => state.products.isOnePlanAvailable,
      isPremiumMonthlyPromoActive: state => state.app.isPremiumMonthlyPromoActive,
      isEDAPremiumPreSaleActive: state => state.app.isEDAPremiumPreSaleActive,
      isSkillDivePreSaleActive: state => state.app.isSkillDivePreSaleActive,
      isSignInModalShown: state => state.app.isSignInModalShown,
      isAuthenticated: state => state.auth.isAuthenticated,
      usersCurrentSubscriptions: state => state.subscriptions.usersCurrentSubscriptions,
      userHasSkillDive: state => state.subscriptions.userHasSkillDive,
    }),
    ...mapGetters({
      newDesign: 'featureFlags/displayNewDesign',
    }),
    isHeaderShown() {
      return !['shop', 'complete', 'skill-dive'].includes(this.$route.name)
    },
    isShopPage() {
      return ['shop', 'skill-dive'].includes(this.$route.name)
    },
    isTrialPage() {
      return this.$route.name === 'free-trial'
    },
    isCompletePage() {
      return this.$route.name === 'complete'
    },
    isAccountPage() {
      return this.$route.name === 'account'
    },
    isPaymentPage() {
      return this.$route.name === 'payment'
    },
    isConfirmationPage() {
      return this.$route.name === 'confirmation'
    },
    isPremiumMonthlyPromoPage() {
      return this.$route.name === 'premium-monthly'
    },
    isEDAPremiumPage() {
      return this.$route.name === 'eeda-presale'
    },
    isSkillDivePreSalePage() {
      return this.$route.name === 'skill-dive'
    },
    isModalOpen() {
      return (
        this.isIn3dsFlow ||
        this.isNewAccountModalShown ||
        this.isEditModalShown ||
        this.isPaymentModalShown ||
        this.isPaymentSelectorModalShown ||
        this.isSignInModalShown
      )
    },
    showShopDefaultHeroSection() {
      const {
        isOnePlanAvailable,
        isSkillDivePreSaleActive,
        newDesign,
        isShopPage,
        isTrialPage,
        isCompletePage,
        isAccountPage,
        isPaymentPage,
        isConfirmationPage,
        isPremiumMonthlyPromoPage,
        isEDAPremiumPage,
      } = this
      if (isOnePlanAvailable) return true
      return (
        newDesign &&
        !isSkillDivePreSaleActive &&
        isShopPage &&
        !isTrialPage &&
        !isCompletePage &&
        !isAccountPage &&
        !isPaymentPage &&
        !isConfirmationPage &&
        !isPremiumMonthlyPromoPage &&
        !isEDAPremiumPage
      )
    },
    showShopFooter() {
      return (
        this.isShopPage ||
        this.isPremiumMonthlyPromoPage ||
        this.isEDAPremiumPage ||
        this.isSkillDivePreSalePage
      )
    },
  },
  methods: {
    ...mapMutations({
      setIsEditModalShown: 'app/IS_EDIT_MODAL_SHOWN_UPDATED',
    }),
  },
}
</script>

<style lang="scss" scoped>
.container {
  $main_bg: '../assets/images/backgrounds/main_bg.jpg';
  $bg-diamonds: '../assets/images/backgrounds/bg-diamonds.png';
  $shop_bg: '../assets/images/backgrounds/shop_bg.png';
  $shop-header-bg: '../assets/images/backgrounds/shop-header-bg.png';
  display: flex;
  flex-flow: column;
  padding: 0 !important;
  position: absolute;
  min-height: unset;
  width: 100%;
  &--trial {
    background: url(#{$bg-diamonds}) !important;
    background-repeat: no-repeat !important;
    background-size: cover !important;
  }
  &--shop {
    background: url(#{$shop_bg}), var(--black-2) url(#{$main_bg});
    background-repeat: no-repeat;
    background-size: cover;
    @include desktop {
      min-height: 150vh;
    }
    @include lg-desktop {
      min-height: unset;
    }
  }
  &--trial,
  &--account,
  &--payment,
  &--confirmation {
    min-height: 100%;
  }
  &--complete {
    min-height: unset;
    height: 100%;
  }
  &--eda-premium-pre-sale {
    background: var(--black-1) !important;
  }
  &--shop-default-hero {
    @include desktop {
      min-height: unset;
      background: url(#{$shop-header-bg}), var(--black-2) url(#{$main_bg});
      background-repeat: no-repeat, no-repeat;
      background-position: top, top;
      background-size: 100%, cover;
    }
    @include lg-desktop {
      background-size: 65%, cover;
    }
  }
  &--skill-dive {
    &-hero {
      background: rgb(110, 11, 248);
      background: radial-gradient(
          circle at 50% 5%,
          rgb(110, 11, 248) 0%,
          rgb(17, 17, 16) 40%,
          rgba(30, 30, 30, 0) 60%
        ),
        linear-gradient(180deg, #1e1e1e 30%, rgba(30, 30, 30, 0) 100%),
        var(--black-2) url(#{$main_bg});
      background-size: 140% 8%, 0, cover;
      background-repeat: no-repeat;
      @include mobile {
        background: radial-gradient(
            circle at 52% 35%,
            rgb(110, 11, 248) 0%,
            rgb(17, 17, 16) 40%,
            rgba(30, 30, 30, 0) 60%
          ),
          linear-gradient(180deg, #1e1e1e 30%, rgba(30, 30, 30, 0) 100%),
          var(--black-2) url(#{$main_bg});
        background-size: 160% 10%, 0, cover;
        background-repeat: no-repeat;
      }
      @include desktop {
        background: radial-gradient(
            circle at 52% 35%,
            rgb(110, 11, 248) 0%,
            rgb(17, 17, 16) 40%,
            rgba(30, 30, 30, 0) 60%
          ),
          linear-gradient(180deg, #1e1e1e 30%, rgba(30, 30, 30, 0) 100%),
          var(--black-2) url(#{$main_bg});
        background-size: 140% 12%, 0, cover;
        background-repeat: no-repeat;
      }
      @include lg-desktop {
        background: radial-gradient(
            circle at 52% 20%,
            rgb(110, 11, 248) 0%,
            rgb(17, 17, 16) 30%,
            rgba(30, 30, 30, 0) 60%
          ),
          linear-gradient(180deg, #1e1e1e 25%, rgba(30, 30, 30, 0) 100%),
          var(--black-2) url(#{$main_bg});
        background-size: 120% 20%, 0, cover;
        background-repeat: no-repeat;
      }
    }
    &-page {
      background: rgb(110, 11, 248);
      background: radial-gradient(
          circle at 50% 5%,
          rgb(110, 11, 248) 0%,
          rgb(17, 17, 16) 40%,
          rgba(30, 30, 30, 0) 60%
        ),
        linear-gradient(180deg, #1e1e1e 30%, rgba(30, 30, 30, 0) 100%), var(--black-2);
      @include mobile {
        background: radial-gradient(
            circle at 90% 20%,
            rgb(110, 11, 248) 0%,
            rgb(17, 17, 16) 40%,
            rgba(30, 30, 30, 0) 60%
          ),
          linear-gradient(180deg, #1e1e1e 30%, rgba(30, 30, 30, 0) 100%), var(--black-2);
      }
      @include desktop {
        background: radial-gradient(
            circle at 70% 20%,
            rgb(110, 11, 248) 0%,
            rgb(17, 17, 16) 40%,
            rgba(30, 30, 30, 0) 60%
          ),
          linear-gradient(180deg, #1e1e1e 30%, rgba(30, 30, 30, 0) 100%), var(--black-2);
      }
      @include lg-desktop {
        background: radial-gradient(
            circle at 60% 10%,
            rgb(110, 11, 248) 0%,
            rgb(17, 17, 16) 30%,
            rgba(30, 30, 30, 0) 60%
          ),
          linear-gradient(180deg, #1e1e1e 25%, rgba(30, 30, 30, 0) 100%), var(--black-2);
      }
    }
  }
}
</style>
